import { useState } from 'react';
import type { IdentityBlock } from '@ni/shared';
import { formatDay, plural } from '../../lib/format';
import { ErrorLine } from '../ui/error-line';
import { WorkingButton } from '../ui/working-button';
import { IDENTITY_CONFIRMED } from '../../lib/copy';

/**
 * The one-line identity summary under the tab bar (ui-design §4.6), and the
 * recourse that belongs on it.
 *
 * Two things were wrong with the flat line it replaces. The anchors were spelled
 * out inline, so a well-anchored identity pushed the recourse off past a comma
 * list nobody reads twice; and "Wrong person" was a text control sitting inside
 * a text line, which is the same badge/action confusion the fact rows carried.
 * So: the state and its date on the left, the anchors behind the system's own
 * disclosure caret, and the action as a bordered button on the right, where the
 * eye already looks for one.
 *
 * The wrong-person control renders only when the route passes a handler — the
 * route withholds it once a dossier has completed, because from there the unwind
 * is code-level-recovery-only territory.
 *
 * `moreOnRecord` is the count of confirmed identifiers the standing version does
 * **not** name (`anchors_on_record` on the record read), which is a state the
 * 2026-09-15 ruling created: an import that attaches to somebody already
 * identified writes their member number after the version was minted, so the
 * anchor is on the record and outside the set. It used to show up nowhere —
 * not in the disclosure above, which lists the set, and not in the
 * not-yet-confirmed line, which only renders when there is no identity at all.
 * Zero for almost everybody, and then the line does not render.
 */
export function IdentityLine({
  identity,
  moreOnRecord = 0,
  onWrongPerson,
  pending = false,
  error,
}: {
  identity: IdentityBlock;
  moreOnRecord?: number;
  onWrongPerson?: () => void;
  pending?: boolean;
  error?: unknown;
}) {
  const [showAnchors, setShowAnchors] = useState(false);
  const anchors = identity.anchors;

  return (
    <div className="identity-line">
      <div className="identity-head">
        <div className="identity-state">
          <span className="state">{IDENTITY_CONFIRMED}</span>
          {formatDay(identity.confirmed_at ?? identity.created_at)}
          {anchors.length > 0 ? (
            <button
              type="button"
              className="linkish flush anchor-toggle"
              aria-expanded={showAnchors}
              onClick={() => setShowAnchors((value) => !value)}
            >
              {plural(anchors.length, 'anchor', 'anchors')}
            </button>
          ) : null}
        </div>

        {onWrongPerson ? (
          <WorkingButton
            label="Wrong person"
            workingLabel="Removing…"
            variant="act"
            tiny
            working={pending}
            onClick={onWrongPerson}
          />
        ) : null}
      </div>

      {moreOnRecord > 0 ? (
        <div className="since-identity">
          {plural(moreOnRecord, 'more identifier', 'more identifiers')} on record, added since
          identity was confirmed.
        </div>
      ) : null}

      {showAnchors && anchors.length > 0 ? (
        <div className="identity-anchors">
          {anchors.map((anchor) => (
            <div key={anchor}>{anchor}</div>
          ))}
        </div>
      ) : null}

      {error ? <ErrorLine error={error} /> : null}
    </div>
  );
}
